import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAMedias } from '../../context/AMediasContext';
import { usePerfil } from '../../context/PerfilContext';
import { formatoEuros } from '../../utils/numeros';
import { adelantosPorPersona, gastoPorPlan, periodoAnio, periodoMes } from '../../utils/informes';
import { pendientePorPersona } from '../../utils/transaccionUtils';
import { BloquePlus, EtiquetaPlus, Tarjeta, Vacio, VistaInforme } from './comun';

const dias = (n: number) => {
  const r = Math.round(n);
  return r === 1 ? '1 día' : `${r} días`;
};

/** I10: lo adelantado a cada persona, lo que tarda en devolver y lo gastado en cada plan de A medias. */
export const SeccionAdelantos: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { esPlus, tieneModulo } = usePerfil();
  const { transacciones } = useData();
  const { planes } = useAMedias();
  const periodo = useMemo(
    () => (vista.mes === null ? periodoAnio(vista.anio) : periodoMes(vista.anio, vista.mes)),
    [vista.anio, vista.mes],
  );
  const personas = useMemo(() => adelantosPorPersona(transacciones, periodo), [transacciones, periodo]);
  const pendiente = useMemo(() => pendientePorPersona(transacciones), [transacciones]);
  const porPlan = useMemo(() => gastoPorPlan(transacciones, periodo), [transacciones, periodo]);

  if (!esPlus) {
    return (
      <BloquePlus
        titulo="Adelantos y A medias"
        texto="Cuánto has adelantado a cada persona, cuánto te ha devuelto, lo que tarda de media en devolver y lo que has gastado en cada plan de A medias."
      />
    );
  }

  const adelantado = personas.reduce((s, p) => s + p.adelantado, 0);
  const devuelto = personas.reduce((s, p) => s + p.devuelto, 0);
  const totalPendiente = pendiente.reduce((s, p) => s + p.importe, 0);
  const plan = (id: string) => planes.find(p => p.id === id);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          { t: 'Adelantado', v: adelantado },
          { t: 'Te han devuelto', v: devuelto },
          { t: 'Te deben hoy', v: totalPendiente },
        ].map(x => (
          <div key={x.t} className="bg-white dark:bg-gray-800 rounded-xl shadow p-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{x.t}</p>
            <p className="text-base md:text-lg font-bold tabular-nums text-gray-900 dark:text-white mt-1">{formatoEuros(x.v)}</p>
          </div>
        ))}
      </div>

      <Tarjeta titulo="Por persona" subtitulo="Lo adelantado y devuelto en el periodo. Lo que tarda en devolver sale de todo tu historial." accion={<EtiquetaPlus />}>
        {personas.length === 0 ? <Vacio>No hay adelantos en este periodo.</Vacio> : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {personas.map(p => {
              const debe = pendiente.find(x => x.persona.toLowerCase() === p.persona.toLowerCase())?.importe ?? 0;
              return (
                <li key={p.persona} className="py-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">{p.persona}</span>
                    <span className="tabular-nums text-gray-900 dark:text-white">{formatoEuros(p.adelantado)}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Te ha devuelto {formatoEuros(p.devuelto)}
                    {Math.abs(debe) >= 0.005 && <> · {debe > 0 ? `te debe ${formatoEuros(debe)}` : `le debes ${formatoEuros(-debe)}`}</>}
                    {p.diasMedios !== null && <> · suele tardar {dias(p.diasMedios)}</>}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Tarjeta>

      {tieneModulo('amedias') && (
        <Tarjeta titulo="Planes de A medias" subtitulo="Tu parte de los gastos de cada plan que pasaron a Finanzas." accion={<EtiquetaPlus />}>
          {porPlan.length === 0 ? <Vacio>No hay gastos de A medias en este periodo.</Vacio> : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {porPlan.map(x => {
                const p = plan(x.planId);
                return (
                  <li key={x.planId} className="flex items-center gap-2 py-2 text-sm">
                    <span className="shrink-0">{p?.icono ?? '🤝'}</span>
                    {p
                      ? <Link to={`/amedias/${p.id}`} className="flex-1 min-w-0 truncate text-gray-900 dark:text-white hover:underline">{p.nombre}</Link>
                      : <span className="flex-1 min-w-0 truncate text-gray-500 dark:text-gray-400">Plan del que ya no formas parte</span>}
                    <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatoEuros(x.total)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Tarjeta>
      )}
    </div>
  );
};
